import { supabase } from './supabaseClient';

function mediaTypeFromFile(file) {
  if (file.type.startsWith('video/')) return 'video';
  return 'image';
}

export async function fetchProductReviews(productId) {
  const { data, error } = await supabase
    .from('product_reviews')
    .select(`
      id,
      product_id,
      user_id,
      rating,
      title,
      body,
      display_name,
      created_at,
      product_review_media (
        id,
        media_url,
        media_type,
        sort_order
      )
    `)
    .eq('product_id', productId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map(review => ({
    ...review,
    media: [...(review.product_review_media || [])].sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
    ),
  }));
}

export function summarizeReviews(reviews) {
  if (!reviews.length) {
    return { average: 0, count: 0 };
  }

  const total = reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0);
  return {
    average: total / reviews.length,
    count: reviews.length,
  };
}

export async function hasPurchasedProduct(productId) {
  const { data, error } = await supabase.rpc('has_purchased_product', {
    p_product_id: productId,
  });

  if (error) {
    // Fallback when RPC is not installed yet
    const { data: sessionData } = await supabase.auth.getUser();
    const userId = sessionData?.user?.id;
    if (!userId) return false;

    const { data: rows, error: orderError } = await supabase
      .from('orders')
      .select(`
        id,
        status,
        order_items!inner ( product_id )
      `)
      .eq('user_id', userId)
      .eq('order_items.product_id', productId);

    if (orderError) return false;

    return (rows || []).some(order =>
      ['completed', 'paid', 'fulfilled', 'shipped', 'delivered'].includes(
        String(order.status || '').toLowerCase()
      )
    );
  }

  return Boolean(data);
}

export async function uploadReviewMedia(file, userId) {
  const originalExt = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
  const ext = originalExt || (file.type.startsWith('video/') ? 'mp4' : 'jpg');
  const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage
    .from('review-media')
    .upload(path, file, { cacheControl: '3600', upsert: false });

  if (error) throw error;

  const { data } = supabase.storage.from('review-media').getPublicUrl(path);
  return {
    media_url: data.publicUrl,
    media_type: mediaTypeFromFile(file),
  };
}

export async function createProductReview({
  productId,
  userId,
  rating,
  title,
  body,
  displayName,
  mediaFiles = [],
}) {
  const { data: review, error } = await supabase
    .from('product_reviews')
    .insert({
      product_id: productId,
      user_id: userId,
      rating,
      title: title || null,
      body,
      display_name: displayName || null,
    })
    .select('id')
    .single();

  if (error) throw error;

  if (mediaFiles.length) {
    const uploaded = [];
    for (const file of mediaFiles) {
      uploaded.push(await uploadReviewMedia(file, userId));
    }

    const rows = uploaded.map((item, index) => ({
      review_id: review.id,
      media_url: item.media_url,
      media_type: item.media_type,
      sort_order: index,
    }));

    const { error: mediaError } = await supabase
      .from('product_review_media')
      .insert(rows);

    if (mediaError) throw mediaError;
  }

  return review.id;
}
